import { Typography } from '@euler/app/components/typography';
import { CouponCampaignDetailSheet } from '@euler/app/flows/ai-suite/cards/CouponCampaignDetailSheet';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { apdmPercent } from '@euler/functions/apdm/campaignFunnel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmCampaignDefinition,
  ApdmCampaignReport,
  ApdmCouponCampaignSummary,
} from '@euler/model/apdm';
import dayjs from 'dayjs';
import { ChevronRight, MessageSquareText, Ticket } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  ApdmCard,
  ApdmIconTile,
  ApdmSectionTitle,
  ApdmTag,
} from '../components/ApdmSurface';
import {
  apdmAudienceSourcesText,
  apdmAudienceText,
  apdmScheduleText,
} from './campaignDetailText';

function Line({ label, value }: { label: string; value: string }) {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ flexDirection: 'row', gap: rpx(12) }}>
      <Typography.Text
        size={rpx(12)}
        color={aiColors.mutedForeground}
        style={{ width: rpx(56) }}
      >
        {label}
      </Typography.Text>
      <Typography.Text
        size={rpx(13)}
        color={aiColors.foreground}
        style={{ flex: 1 }}
      >
        {value}
      </Typography.Text>
    </View>
  );
}

/**
 * What the campaign sends and to whom, read only: it is changed by talking
 * to the AI in its conversation.
 */
export const ContentTab = memo(
  ({
    definition,
    report,
    coupon,
    audience,
    onEdit,
  }: {
    /** Null for an advisor, who sees only their customers. */
    definition: ApdmCampaignDefinition | null;
    report: ApdmCampaignReport | null;
    coupon: ApdmCouponCampaignSummary | null;
    audience: number;
    /** Opens the conversation; null when there is none. */
    onEdit: (() => void) | null;
  }) => {
    const { rpx } = useResponsiveContext();
    const [couponOpen, setCouponOpen] = useState(false);
    if (!definition)
      return (
        <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
          活动内容由负责人管理。
        </Typography.Text>
      );
    // Each version's numbers come from the report's per-text breakdown,
    // matched by the text the version sends.
    const variantMetrics = (text: string) =>
      report?.variants.find(row => text.startsWith(row.label))?.metrics ?? null;
    return (
      <View style={{ gap: rpx(12) }}>
        <ApdmCard style={{ padding: rpx(16), gap: rpx(12) }}>
          <ApdmSectionTitle
            title="消息内容"
            note={`${definition.variants.length} 个版本，每位客户收到其中一条`}
          />
          {definition.variants.map((variant, index) => {
            const metrics = variantMetrics(variant.text);
            return (
              <View key={index} style={{ gap: rpx(6) }}>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: rpx(8),
                  }}
                >
                  <ApdmIconTile
                    icon={MessageSquareText}
                    color={aiColors.primary}
                    size={24}
                  />
                  <Typography.Text
                    medium
                    size={rpx(12)}
                    color={aiColors.foreground}
                    style={{ flex: 1 }}
                  >
                    第 {index + 1} 条
                  </Typography.Text>
                  {metrics ? (
                    <>
                      <ApdmTag
                        text={`回复率 ${apdmPercent(
                          metrics.replied,
                          metrics.reached,
                        )}`}
                        color={aiColors.primary}
                      />
                      <ApdmTag
                        text={`转化 ${metrics.converted}`}
                        color={aiColors.green}
                      />
                    </>
                  ) : null}
                </View>
                <View
                  style={{
                    padding: rpx(12),
                    borderRadius: rpx(12),
                    backgroundColor: aiColors.input,
                  }}
                >
                  <Typography.Text
                    size={rpx(13)}
                    lineHeight={rpx(20)}
                    color={aiColors.foreground}
                  >
                    {variant.text}
                  </Typography.Text>
                </View>
              </View>
            );
          })}
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            花括号里的内容会换成每位客户自己的信息，例如 {'{plateNo}'}{' '}
            是车牌号。
          </Typography.Text>
        </ApdmCard>
        {coupon ? (
          <ApdmCard
            onPress={() => setCouponOpen(true)}
            accessibilityLabel={coupon.title}
            style={{
              padding: rpx(16),
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(12),
            }}
          >
            <ApdmIconTile icon={Ticket} color={aiColors.primary} size={36} />
            <View style={{ flex: 1, gap: rpx(2) }}>
              <Typography.Text
                medium
                size={rpx(14)}
                color={aiColors.foreground}
              >
                {coupon.title}
              </Typography.Text>
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                {dayjs(coupon.startsAt).format('M月D日')} –{' '}
                {dayjs(coupon.endsAt).format('M月D日')} · 已核销{' '}
                {coupon.redeemedCount}
              </Typography.Text>
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                每位客户会收到带专属二维码的海报，到店后扫码核销。
              </Typography.Text>
            </View>
            <ChevronRight size={16} color={aiColors.mutedForeground} />
          </ApdmCard>
        ) : null}
        <ApdmCard style={{ padding: rpx(16), gap: rpx(10) }}>
          <ApdmSectionTitle title="发给谁、什么时候" />
          <Line
            label="客户"
            value={`${apdmAudienceText(
              definition.audience.rule,
            )} · ${audience.toLocaleString('zh-CN')} 位`}
          />
          <Line
            label="来源"
            value={apdmAudienceSourcesText(definition.audience.sources)}
          />
          <Line label="时间" value={apdmScheduleText(definition.schedule)} />
        </ApdmCard>
        {onEdit ? (
          <Pressable
            accessibilityRole="button"
            onPress={onEdit}
            style={{ alignSelf: 'center', padding: rpx(8) }}
          >
            <Typography.Text medium size={rpx(13)} color={aiColors.primary}>
              在对话中修改
            </Typography.Text>
          </Pressable>
        ) : null}
        <CouponCampaignDetailSheet
          coupon={couponOpen ? coupon : null}
          onClose={() => setCouponOpen(false)}
        />
      </View>
    );
  },
);
